<script setup>
  import {ref} from "vue";

  import {useRemember} from "@inertiajs/vue3";
  import PagesRecordResultOverviewGreenlights from "@pages/Records/Components/Result/Overview/Greenlights.vue";
  import PagesRecordResultOverviewRedlights from "@pages/Records/Components/Result/Overview/Redlights.vue";
  import {MedCheckbox, MedFormField, MedLabel} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PagesRecordResultOverview",
  });

  const props = defineProps({
    record: {
      type: Object,
      required: true,
    },
    greenlights: {
      type: Array,
      default: () => {
        return [];
      },
    },
    symptomaticRedlights: {
      type: Array,
      default: () => {
        return [];
      },
    },
    prognosticRedlights: {
      type: Array,
      default: () => {
        return [];
      },
    },
    nonComplianceRedlights: {
      type: Array,
      default: () => {
        return [];
      },
    },
    preventionRedlights: {
      type: Array,
      default: () => {
        return [];
      },
    },
  });

  const printSettings = useRemember(
    {
      doctor: true,
      patient: true,
    },
    `PrintSettings/Overview:${props.record.id}`,
  );
  const highlightSection = ref(false);
</script>

<template>
  <section
    :class="{
      'in-[.print-doc]:print:hidden': !printSettings.doctor,
      'in-[.print-patient]:print:hidden': !printSettings.patient,
      'bg-yellow-50': highlightSection,
    }"
    :data-test="getTestId('overview')"
  >
    <h2 class="cursor-default text-center text-2xl text-gray-500">Übersicht</h2>
    <div class="grid justify-end print:hidden">
      <div
        class="text-right text-sm"
        role="button"
        tabindex="0"
        @mouseover="highlightSection = true"
        @mouseout="highlightSection = false"
        @focusin="highlightSection = true"
        @focusout="highlightSection = false"
      >
        <div>Übersicht übernehmen in PDF</div>
        <div class="flex justify-end space-x-4">
          <MedFormField orientation="horizontal">
            <MedCheckbox v-model="printSettings.doctor" :data-test="getTestId('overview-print-doc-checkbox')" />
            <MedLabel>an Arzt</MedLabel>
          </MedFormField>

          <MedFormField orientation="horizontal">
            <MedCheckbox v-model="printSettings.patient" :data-test="getTestId('overview-print-patient-checkbox')" />
            <MedLabel>an Patienten</MedLabel>
          </MedFormField>
        </div>
      </div>
    </div>

    <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 print:break-before-avoid-page">
      <PagesRecordResultOverviewGreenlights :checks="greenlights" :test-id="getTestId('overview')" />

      <div class="space-y-4">
        <PagesRecordResultOverviewRedlights
          v-if="symptomaticRedlights.length > 0"
          anchor-prefix="symptomatic"
          title="Aktuelle Probleme / Symptome"
          :checks="symptomaticRedlights"
          :test-id="getTestId('overview-symptomatic')"
        />

        <PagesRecordResultOverviewRedlights
          v-if="prognosticRedlights.length > 0"
          anchor-prefix="prognostic"
          title="Potentielle Risiken"
          :checks="prognosticRedlights"
          :test-id="getTestId('overview-prognostic')"
        />

        <PagesRecordResultOverviewRedlights
          v-if="nonComplianceRedlights.length > 0"
          anchor-prefix="non_compliance"
          title="Adhärenz"
          :checks="nonComplianceRedlights"
          :test-id="getTestId('overview-non-compliance')"
        />

        <PagesRecordResultOverviewRedlights
          v-if="preventionRedlights.length > 0"
          anchor-prefix="prevention"
          title="Prävention"
          :checks="preventionRedlights"
          :test-id="getTestId('overview-prevention')"
        />
      </div>
    </div>
  </section>
</template>
